import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { serviceWizardSearchRequestService } from '../../../services/api/serviceWizardSearchRequestService';
import { accountService } from '../../../services/api/accountService';
import { setSendInquiryResume } from '../../../utils/sendInquiryResume';
import { persistActiveWizardSearchRequest } from '../../../utils/persistActiveWizardSearchRequest';
import {
  buildSearchWizardState,
  getSearchTaskLabel,
  getSearchTimingLabel,
  getSearchTimingOptions,
  getSearchTradeLabel,
} from '../../../utils/serviceSearch';
import {
  formatPhoneInputMask,
  phoneDigitsForApi,
} from '../../../utils/phoneInputMask';
import {
  getSignupEmailError,
  getSignupPhoneError,
  SIGNUP_EMAIL_REGEX,
} from '../../../utils/signupFieldValidation';
import { getNavigateToLoginOptions } from '../../../utils/authSessionExpired';
import {
  readLatestWizardRequesterCache,
  readWizardRequesterCache,
  writeWizardRequesterCache,
  wizardRequesterPatchMatchesSaved,
} from '../../../utils/wizardRequesterSessionCache';
import { toPersonNameCase } from '../../../utils/normalizePersonName';
import {
  getServiceLocationErrors,
  isServiceLocationComplete,
  readLastServiceLocation,
  writeLastServiceLocation,
} from '../../../utils/serviceLocation';
import WizardRequestSummaryDialog from '../WizardRequestSummaryDialog/WizardRequestSummaryDialog';
import ServiceLocationFields from '../ServiceLocationFields/ServiceLocationFields';
import NkySpinner from '../NkySpinner/NkySpinner';
import {
  StyledContactDialog,
  ContactModalTitle,
  ContactModalCloseButton,
  ContactModalContent,
  ContactLeadText,
  GuestFormSection,
  GuestFormGrid,
  ContactField,
  ContactModalActions,
  CancelModalButton,
  SendModalButton,
  ContactErrorAlert,
  ContactSearchReferencePanel,
  ContactSearchReferencePanelTrigger,
  ContactSearchReferenceTitle,
  ContactSearchRefRow,
  ContactSearchRefLabel,
  ContactSearchRefValue,
  ContactExistingAccountPanel,
  ContactExistingAccountIconWrap,
  ContactExistingAccountBody,
  ContactExistingAccountTitle,
  ContactExistingAccountMessage,
  ContactSignInButton,
  ContactDisclaimerText,
  ContactMethodSection,
  ContactMethodLabel,
  ContactMethodOptions,
  ContactMethodButton,
  ContactMethodSavedNote,
  ContactChoiceOptions,
  ContactSheetSection,
  AlsoSendPanel,
  AlsoSendNames,
  ConfirmationHero,
  ConfirmationTitle,
  ConfirmationText,
  ConfirmationDetails,
  ConfirmationList,
} from './ContactForServiceModal.styled';

const PREFERRED_CONTACT_METHODS = ['email', 'phone', 'text'];

function isPreferredContactMethod(value) {
  return PREFERRED_CONTACT_METHODS.includes(value);
}

function preferredContactMethodLabel(method) {
  if (method === 'email') return 'email';
  if (method === 'phone') return 'phone';
  if (method === 'text') return 'text';
  return method;
}

/** Empty allowed; partial/incomplete phone shows standard phone error when non-empty. */
function optionalPhoneError(phoneValue) {
  return getSignupPhoneError(phoneValue, { requiredLabel: '' });
}

function displayRefValue(value) {
  if (value == null) return '—';
  const s = String(value).trim();
  return s.length ? s : '—';
}

function formatPhoneForDisplay(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return String(raw || '').trim();
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function draftTaskLabel(draft) {
  return (
    getSearchTaskLabel(draft.serviceType, draft.subServiceType) ||
    `Any ${getSearchTradeLabel(draft.serviceType) || 'service'} job`
  );
}

const EMPTY_LOCATION = { city: '', state: '' };

function joinNames(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Contact sheet for one provider (opens over results / storefront so the page keeps its place).
 *
 * Two modes:
 * - **Draft** (`draftSearch`): the service search has not been saved yet. The sheet collects the
 *   job's city and state, timing when the search has none, and an optional note (only the
 *   homeowner's own search text is carried in); on send it saves the search request, then sends it.
 * - **Existing** (`wizardSearchRequestId`): an already-saved search request is sent as-is.
 *
 * Guests PATCH requester fields before sending (same tab reuses saved contact via sessionStorage);
 * logged-in users send only (backend fills requester from the account).
 * After sending, the sheet shows a confirmation with the provider's phone and quoting method.
 */
const ContactForServiceModal = ({
  open,
  onClose,
  businessId,
  businessName,
  businessPhone,
  quotingMethodLabels,
  hasFreeQuotes,
  wizardSearchRequestId,
  draftSearch,
  additionalRecipients,
  clientSessionId,
  user,
  onSent,
  serviceLabel,
  subserviceLabel,
  urgencyLabel,
  wizardState,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDraft = Boolean(draftSearch) && !wizardSearchRequestId;
  const extraRecipients = (additionalRecipients || []).filter(
    (r) => r?.BusinessId && r.BusinessId !== businessId,
  );
  const askTiming = isDraft && !draftSearch.timing;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('');
  /** True when the logged-in account already has a saved preference (do not re-ask). */
  const [contactMethodLocked, setContactMethodLocked] = useState(false);
  /** True while fetching account preference for a logged-in user. */
  const [contactMethodLoading, setContactMethodLoading] = useState(false);
  /** Answer to the sheet's own timing question; the search's answer wins when it has one. */
  const [timing, setTiming] = useState('');
  const requestTiming = askTiming ? timing : draftSearch?.timing || '';
  const [note, setNote] = useState('');
  const [jobLocation, setJobLocation] = useState(EMPTY_LOCATION);
  const [locationErrors, setLocationErrors] = useState({ city: '', state: '' });
  const [alsoSend, setAlsoSend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  /** 409 duplicate send — friendly warning, not an error */
  const [sendDuplicateWarning, setSendDuplicateWarning] = useState(null);
  /** true after PATCH 403 — email already tied to an account */
  const [emailHasAccount, setEmailHasAccount] = useState(false);
  const [requestSummaryOpen, setRequestSummaryOpen] = useState(false);
  const [guestFieldErrors, setGuestFieldErrors] = useState({
    email: '',
    phone: '',
  });
  /** Request saved by this sheet (draft mode), reused for the sign-in resume. */
  const [createdRequestId, setCreatedRequestId] = useState(null);
  /** Per-recipient outcomes after a send; non-null shows the confirmation view. */
  const [sentOutcomes, setSentOutcomes] = useState(null);

  const activeRequestId = wizardSearchRequestId || createdRequestId;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSendDuplicateWarning(null);
    setEmailHasAccount(false);
    setGuestFieldErrors({ email: '', phone: '' });
    setPreferredContactMethod('');
    setContactMethodLocked(false);
    setSentOutcomes(null);
    setCreatedRequestId(null);
    setAlsoSend(false);
    setJobLocation(readLastServiceLocation() || EMPTY_LOCATION);
    setLocationErrors({ city: '', state: '' });
    setTiming('');
    setNote(String(draftSearch?.note || '').trim());
    if (!user) {
      setContactMethodLoading(false);
      const cached =
        readWizardRequesterCache(wizardSearchRequestId) || readLatestWizardRequesterCache();
      setFirstName(cached?.firstName || '');
      setLastName(cached?.lastName || '');
      setEmail(cached?.email || '');
      setPhone(formatPhoneInputMask(cached?.phoneInput ?? ''));
      return;
    }
    let cancelled = false;
    setContactMethodLoading(true);
    (async () => {
      try {
        const acc = await accountService.getAccount();
        if (cancelled) return;
        setFirstName(acc.FirstName || '');
        setLastName(acc.LastName || '');
        setEmail(acc.Email || '');
        setPhone(formatPhoneInputMask(acc.PhoneNumber || ''));
        if (isPreferredContactMethod(acc.PreferredContactMethod)) {
          setPreferredContactMethod(acc.PreferredContactMethod);
          setContactMethodLocked(true);
        }
      } catch {
        if (cancelled) return;
        setFirstName('');
        setLastName('');
        setEmail(user.Email || '');
        setPhone('');
      } finally {
        if (!cancelled) setContactMethodLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Reset only when the sheet opens for a provider; draft fields are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, wizardSearchRequestId, businessId]);

  useEffect(() => {
    if (!open) setRequestSummaryOpen(false);
  }, [open]);

  const handlePreferredContactMethodChange = (method) => {
    if (contactMethodLocked) return;
    setPreferredContactMethod(method);
    setGuestFieldErrors({ email: '', phone: '' });
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailHasAccount(false);
    if (guestFieldErrors.email) {
      setGuestFieldErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneInputMask(e.target.value));
    if (guestFieldErrors.phone) {
      setGuestFieldErrors((prev) => ({ ...prev, phone: '' }));
    }
  };

  const handleGuestPhoneBlur = () => {
    setGuestFieldErrors((prev) => ({
      ...prev,
      phone:
        preferredContactMethod === 'phone' || preferredContactMethod === 'text'
          ? getSignupPhoneError(phone)
          : optionalPhoneError(phone),
    }));
  };

  const handleGoToSignIn = () => {
    setSendInquiryResume({
      returnPath: `${location.pathname}${location.search}`,
      businessId,
      businessName,
      wizardSearchRequestId: activeRequestId || undefined,
      clientSessionId: clientSessionId || undefined,
    });
    onClose();
    navigate(
      '/login',
      getNavigateToLoginOptions(location),
    );
  };

  /** Draft mode: the job's location and timing are required before saving the request. */
  const validateDraftAnswers = () => {
    setLocationErrors(getServiceLocationErrors(jobLocation));
    if (!isServiceLocationComplete(jobLocation)) {
      throw new Error('Please enter the city and state where the job is.');
    }
    if (!requestTiming) {
      throw new Error('Please choose when you need help.');
    }
  };

  const handleLocationChange = (next) => {
    setLocationErrors((prev) => ({
      city: next.city !== jobLocation.city ? '' : prev.city,
      state: next.state !== jobLocation.state ? '' : prev.state,
    }));
    setJobLocation(next);
  };

  /** Validates guest fields; returns the PATCH payload (or null for logged-in users). */
  const validateRequester = () => {
    if (!preferredContactMethod) {
      throw new Error("Please select how you'd like to be contacted.");
    }
    if (user) return null;
    if (!clientSessionId) {
      throw new Error('Session expired. Refresh the page and try again.');
    }
    if (!firstName.trim() || !lastName.trim()) {
      throw new Error('Please enter your first and last name.');
    }
    const emailErr =
      preferredContactMethod === 'email'
        ? getSignupEmailError(email)
        : '';
    const phoneErr =
      preferredContactMethod === 'phone' || preferredContactMethod === 'text'
        ? getSignupPhoneError(phone)
        : optionalPhoneError(phone);
    setGuestFieldErrors({ email: emailErr, phone: phoneErr });
    if (emailErr || phoneErr) {
      throw new Error('Please correct your email or phone number.');
    }
    const emailTrim = email.trim();
    const digits = phoneDigitsForApi(phone);
    const normalizedFirstName = toPersonNameCase(firstName);
    const normalizedLastName = toPersonNameCase(lastName);
    setFirstName(normalizedFirstName);
    setLastName(normalizedLastName);
    return {
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      email:
            preferredContactMethod === 'email' && SIGNUP_EMAIL_REGEX.test(emailTrim)
              ? emailTrim
              : '',
      ...(digits.length >= 10 ? { phone: digits } : {}),
    };
  };

  const saveGuestRequester = async (requestId, patchPayload) => {
    const fingerprint = {
      firstName: patchPayload.firstName,
      lastName: patchPayload.lastName,
      email: patchPayload.email || '',
      phone: patchPayload.phone != null ? String(patchPayload.phone) : '',
    };
    const cached = readWizardRequesterCache(requestId);
    if (
      cached?.savedPatchFingerprint &&
      wizardRequesterPatchMatchesSaved(fingerprint, cached.savedPatchFingerprint)
    ) {
      return;
    }
    await serviceWizardSearchRequestService.patchRequester(requestId, {
      clientSessionId,
      ...patchPayload,
    });
    writeWizardRequesterCache(requestId, {
      firstName: patchPayload.firstName,
      lastName: patchPayload.lastName,
      email: patchPayload.email || '',
      phoneInput: phone,
      savedPatchFingerprint: fingerprint,
    });
  };

  const handleSubmit = async () => {
    if (!businessId || (!wizardSearchRequestId && !isDraft)) {
      setError('Missing request context. Return to the service search and try again.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSendDuplicateWarning(null);
    try {
      if (isDraft) validateDraftAnswers();
      const patchPayload = validateRequester();

      let requestId = activeRequestId;
      if (!requestId) {
        requestId = await persistActiveWizardSearchRequest(
          buildSearchWizardState({
            serviceType: draftSearch.serviceType,
            subServiceType: draftSearch.subServiceType,
            timing: requestTiming,
            note,
            city: jobLocation.city,
            state: jobLocation.state,
          }),
        );
        writeLastServiceLocation(jobLocation);
        if (!requestId) throw new Error('Could not save your request. Please try again.');
        setCreatedRequestId(requestId);
      }

      if (patchPayload) await saveGuestRequester(requestId, patchPayload);

      const sendBody = (targetId) => ({
        businessId: targetId,
        preferredContactMethod,
        ...(clientSessionId && !user ? { clientSessionId } : {}),
      });

      const recipients = [
        { BusinessId: businessId, BusinessName: businessName || 'This business' },
        ...(alsoSend ? extraRecipients : []),
      ];
      const outcomes = [];
      for (const [index, recipient] of recipients.entries()) {
        try {
          await serviceWizardSearchRequestService.sendToBusiness(
            requestId,
            sendBody(recipient.BusinessId),
          );
          outcomes.push({ ...recipient, status: 'sent' });
        } catch (e) {
          const duplicate = e.status === 409 || /already sent/i.test(String(e.message || ''));
          if (duplicate) {
            outcomes.push({ ...recipient, status: 'already' });
          } else if (index === 0) {
            throw e;
          } else {
            outcomes.push({ ...recipient, status: 'failed' });
          }
        }
      }

      if (!outcomes.some((o) => o.status === 'sent')) {
        setSendDuplicateWarning(
          'You have already sent this business a message. They will reach out as soon as possible.',
        );
        return;
      }
      setSentOutcomes(outcomes);
      if (onSent) onSent(outcomes);
    } catch (e) {
      if (!user && e.status === 403) {
        setEmailHasAccount(true);
        setError(null);
      } else {
        setError(e.message || 'Request failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = businessName
    ? `Request a quote from ${businessName}`
    : 'Request a quote';

  const guestBlockedByExistingAccount = !user && emailHasAccount;
  const sendDisabled =
    submitting ||
    guestBlockedByExistingAccount ||
    contactMethodLoading;

  const phoneSelected = preferredContactMethod === 'phone' || preferredContactMethod === 'text';
  const emailField = (
    <ContactField
      key="email-field"
      className="guest-form-full-row"
      label="Email"
      type="email"
      name="email"
      value={email}
      onChange={handleEmailChange}
      error={Boolean(guestFieldErrors.email)}
      helperText={guestFieldErrors.email || undefined}
      required={preferredContactMethod === 'email'}
      fullWidth
      variant="outlined"
      size="small"
      disabled={guestBlockedByExistingAccount}
      autoComplete="email"
    />
  );
  const phoneField = (
    <ContactField
      key="phone-field"
      className="guest-form-full-row"
      label={`Phone${preferredContactMethod === 'email' ? ' (optional)' : ''}`}
      name="phone"
      value={phone}
      onChange={handlePhoneChange}
      onBlur={handleGuestPhoneBlur}
      error={Boolean(guestFieldErrors.phone)}
      helperText={guestFieldErrors.phone || undefined}
      required={phoneSelected}
      fullWidth
      variant="outlined"
      size="small"
      disabled={guestBlockedByExistingAccount}
      placeholder="555-123-4567"
      inputMode="tel"
      autoComplete="tel"
    />
  );

  const recipientNames = [
    businessName || 'the business',
    ...(alsoSend ? extraRecipients.map((r) => r.BusinessName || 'another pro') : []),
  ];

  const renderDraftSummary = () => (
    <ContactSearchReferencePanel>
      <ContactSearchReferenceTitle>Your request</ContactSearchReferenceTitle>
      <ContactSearchRefRow>
        <ContactSearchRefLabel>Service</ContactSearchRefLabel>
        <ContactSearchRefValue>
          {displayRefValue(getSearchTradeLabel(draftSearch.serviceType))}
        </ContactSearchRefValue>
      </ContactSearchRefRow>
      <ContactSearchRefRow>
        <ContactSearchRefLabel>Task</ContactSearchRefLabel>
        <ContactSearchRefValue>{draftTaskLabel(draftSearch)}</ContactSearchRefValue>
      </ContactSearchRefRow>
      {!askTiming ? (
        <ContactSearchRefRow>
          <ContactSearchRefLabel>Timing</ContactSearchRefLabel>
          <ContactSearchRefValue>{getSearchTimingLabel(draftSearch.timing)}</ContactSearchRefValue>
        </ContactSearchRefRow>
      ) : null}
    </ContactSearchReferencePanel>
  );

  const renderExistingSummary = () => (
    <ContactSearchReferencePanelTrigger
      type="button"
      onClick={() => setRequestSummaryOpen(true)}
      aria-label="View full request summary"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.25,
        }}
      >
        <ContactSearchReferenceTitle style={{ marginBottom: 0 }}>
          Your request
        </ContactSearchReferenceTitle>
        <ChevronRightIcon sx={{ fontSize: 20, color: 'rgba(90, 100, 120, 0.65)', flexShrink: 0 }} />
      </Box>
      <ContactSearchRefRow>
        <ContactSearchRefLabel>Service</ContactSearchRefLabel>
        <ContactSearchRefValue>{displayRefValue(serviceLabel)}</ContactSearchRefValue>
      </ContactSearchRefRow>
      <ContactSearchRefRow>
        <ContactSearchRefLabel>Category</ContactSearchRefLabel>
        <ContactSearchRefValue>{displayRefValue(subserviceLabel)}</ContactSearchRefValue>
      </ContactSearchRefRow>
      <ContactSearchRefRow>
        <ContactSearchRefLabel>Urgency</ContactSearchRefLabel>
        <ContactSearchRefValue>{displayRefValue(urgencyLabel)}</ContactSearchRefValue>
      </ContactSearchRefRow>
    </ContactSearchReferencePanelTrigger>
  );

  const renderConfirmation = () => {
    const primary = sentOutcomes[0];
    const others = sentOutcomes.slice(1);
    const phoneDisplay = formatPhoneForDisplay(businessPhone);
    const quoting = [
      ...(hasFreeQuotes ? ['Free quotes'] : []),
      ...(quotingMethodLabels || []),
    ];
    return (
      <>
        <ConfirmationHero role="status">
          <CheckCircleRoundedIcon aria-hidden />
          <Box>
            <ConfirmationTitle>Request sent</ConfirmationTitle>
            <ConfirmationText>
              {primary.status === 'sent'
                ? `${primary.BusinessName} has your request and will reach out by ${preferredContactMethodLabel(preferredContactMethod)}.`
                : `You had already sent ${primary.BusinessName} this request.`}
            </ConfirmationText>
          </Box>
        </ConfirmationHero>
        {phoneDisplay || quoting.length > 0 ? (
          <ConfirmationDetails>
            {phoneDisplay ? (
              <ConfirmationText>
                Want to talk now? Call{' '}
                <a href={`tel:${String(businessPhone).replace(/\D/g, '')}`}>{phoneDisplay}</a>
              </ConfirmationText>
            ) : null}
            {quoting.length > 0 ? (
              <ConfirmationText sx={{ mt: phoneDisplay ? 1 : 0 }}>
                How they quote: <strong>{quoting.join(' · ')}</strong>
              </ConfirmationText>
            ) : null}
          </ConfirmationDetails>
        ) : null}
        {others.length > 0 ? (
          <ConfirmationDetails>
            <ConfirmationText>Also sent to:</ConfirmationText>
            <ConfirmationList>
              {others.map((o) => (
                <li key={o.BusinessId}>
                  {o.BusinessName}
                  {o.status === 'already' ? ' (already had your request)' : ''}
                  {o.status === 'failed' ? ' (could not send — try again from their card)' : ''}
                </li>
              ))}
            </ConfirmationList>
          </ConfirmationDetails>
        ) : null}
        {!user ? (
          <ContactDisclaimerText>
            Want to keep track of your requests?{' '}
            <Link to="/sign-up">Create a free account</Link>.
          </ContactDisclaimerText>
        ) : null}
      </>
    );
  };

  return (
    <>
    <StyledContactDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="send-inquiry-dialog-title"
    >
      <ContactModalTitle id="send-inquiry-dialog-title">
        <span className="contact-modal-icon-wrap" aria-hidden>
          <SendRoundedIcon className="contact-modal-icon" />
        </span>
        <span>{title}</span>
        <ContactModalCloseButton
          aria-label="Close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </ContactModalCloseButton>
      </ContactModalTitle>

      <ContactModalContent>
        {sentOutcomes ? renderConfirmation() : (
          <>
        <ContactLeadText>
          We&apos;ll send {businessName || 'them'} your request and the best way to reach you. No
          account needed.
        </ContactLeadText>

        {isDraft ? renderDraftSummary() : renderExistingSummary()}

        {sendDuplicateWarning && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            {sendDuplicateWarning}
          </Alert>
        )}

        {error && !guestBlockedByExistingAccount && (
          <ContactErrorAlert severity="error">{error}</ContactErrorAlert>
        )}

        {!user && guestBlockedByExistingAccount && (
          <ContactExistingAccountPanel role="status">
            <ContactExistingAccountIconWrap aria-hidden>
              <LoginRoundedIcon />
            </ContactExistingAccountIconWrap>
            <ContactExistingAccountBody>
              <ContactExistingAccountTitle>Account found</ContactExistingAccountTitle>
              <ContactExistingAccountMessage>
                An account already exists for this email. Sign in to send your inquiry with your
                saved profile—we&apos;ll return you here after you log in.
              </ContactExistingAccountMessage>
              <ContactSignInButton variant="contained" disableElevation onClick={handleGoToSignIn}>
                Sign in
              </ContactSignInButton>
            </ContactExistingAccountBody>
          </ContactExistingAccountPanel>
        )}

        {isDraft ? (
          <ContactSheetSection>
            <ServiceLocationFields
              city={jobLocation.city}
              state={jobLocation.state}
              errors={locationErrors}
              onChange={handleLocationChange}
              disabled={guestBlockedByExistingAccount}
            />
            {askTiming ? (
              <Box>
                <ContactMethodLabel>When do you need help?</ContactMethodLabel>
                <ContactChoiceOptions role="radiogroup" aria-label="Timing">
                  {getSearchTimingOptions().map((option) => (
                    <ContactMethodButton
                      key={option.id}
                      type="button"
                      role="radio"
                      aria-checked={timing === option.id}
                      $selected={timing === option.id}
                      onClick={() => setTiming(option.id)}
                    >
                      {option.searchLabel}
                    </ContactMethodButton>
                  ))}
                </ContactChoiceOptions>
              </Box>
            ) : null}
            <ContactField
              label="Note for the pro (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything that helps them quote, like the size of the job or access details"
              multiline
              minRows={2}
              fullWidth
              size="small"
              inputProps={{ maxLength: 1000 }}
            />
          </ContactSheetSection>
        ) : null}

        <ContactMethodSection>
          {contactMethodLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <NkySpinner size="sm" label="Loading preferred contact method" />
            </Box>
          ) : contactMethodLocked ? (
            <>
              <ContactMethodSavedNote>
                Your preferred contact method is{' '}
                <strong>{preferredContactMethodLabel(preferredContactMethod)}</strong>.
              </ContactMethodSavedNote>
              <ContactMethodSavedNote>
                If you would like to edit this, you can do so on the{' '}
                <Link to="/account" target="_blank" rel="noopener noreferrer">
                  account page
                </Link>
                .
              </ContactMethodSavedNote>
            </>
          ) : (
            <>
              <ContactMethodLabel>How should they contact you?</ContactMethodLabel>
              <ContactMethodOptions role="radiogroup" aria-label="Preferred contact method">
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'email'}
                  $selected={preferredContactMethod === 'email'}
                  onClick={() => handlePreferredContactMethodChange('email')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <EmailRoundedIcon />
                  Email
                </ContactMethodButton>
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'phone'}
                  $selected={preferredContactMethod === 'phone'}
                  onClick={() => handlePreferredContactMethodChange('phone')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <LocalPhoneRoundedIcon />
                  Phone
                </ContactMethodButton>
                <ContactMethodButton
                  type="button"
                  role="radio"
                  aria-checked={preferredContactMethod === 'text'}
                  $selected={preferredContactMethod === 'text'}
                  onClick={() => handlePreferredContactMethodChange('text')}
                  disabled={guestBlockedByExistingAccount}
                >
                  <SmsRoundedIcon />
                  Text
                </ContactMethodButton>
              </ContactMethodOptions>
            </>
          )}
        </ContactMethodSection>

        {!user && (
          <GuestFormSection>
            <GuestFormGrid>
              <ContactField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={(e) => {
                  const normalized = toPersonNameCase(e.target.value);
                  if (normalized !== e.target.value) setFirstName(normalized);
                }}
                required
                fullWidth
                variant="outlined"
                size="small"
                disabled={guestBlockedByExistingAccount}
              />
              <ContactField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={(e) => {
                  const normalized = toPersonNameCase(e.target.value);
                  if (normalized !== e.target.value) setLastName(normalized);
                }}
                required
                fullWidth
                variant="outlined"
                size="small"
                disabled={guestBlockedByExistingAccount}
              />
              {phoneSelected ? (
                phoneField
              ) : (
                <>
                  {emailField}
                  {phoneField}
                </>
              )}
              <Box className="guest-form-full-row" sx={{ mt: -0.5 }}>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0, lineHeight: 1.45 }}>
                  {preferredContactMethod === 'email' &&
                    'Email is required since that’s your preferred contact method. Phone is optional.'}
                  {phoneSelected &&
                    'Phone is required since that’s your preferred contact method.'}
                  {!preferredContactMethod &&
                    'Select how you’d like to be contacted above, then fill in that field below.'}
                </Typography>
              </Box>
            </GuestFormGrid>
          </GuestFormSection>
        )}

        {extraRecipients.length > 0 ? (
          <AlsoSendPanel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={alsoSend}
                  onChange={(e) => setAlsoSend(e.target.checked)}
                  size="small"
                />
              }
              label={
                <span>
                  Also send to {extraRecipients.length} more top{' '}
                  {extraRecipients.length === 1 ? 'match' : 'matches'}
                  <AlsoSendNames>
                    {extraRecipients.map((r) => r.BusinessName).join(' · ')}
                  </AlsoSendNames>
                </span>
              }
            />
          </AlsoSendPanel>
        ) : null}

        <ContactDisclaimerText>
          By sending, your contact information will be shared with {joinNames(recipientNames)} to
          fulfill your service request. See our{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
          .
        </ContactDisclaimerText>
          </>
        )}
      </ContactModalContent>

      <ContactModalActions>
        {sentOutcomes ? (
          <SendModalButton variant="contained" color="inherit" disableElevation onClick={onClose}>
            Done
          </SendModalButton>
        ) : (
          <>
            <CancelModalButton
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </CancelModalButton>
            <SendModalButton
              variant="contained"
              color="inherit"
              disableElevation
              onClick={handleSubmit}
              disabled={sendDisabled}
            >
              {submitting ? (
                <NkySpinner size="sm" onDark label="Sending request" />
              ) : (
                'Send request'
              )}
            </SendModalButton>
          </>
        )}
      </ContactModalActions>
    </StyledContactDialog>

    {!isDraft ? (
      <WizardRequestSummaryDialog
        open={requestSummaryOpen}
        onClose={() => setRequestSummaryOpen(false)}
        wizardState={wizardState}
        serviceLabel={serviceLabel}
        subserviceLabel={subserviceLabel}
        urgencyLabel={urgencyLabel}
      />
    ) : null}
    </>
  );
};

ContactForServiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  businessId: PropTypes.string,
  businessName: PropTypes.string,
  /** Shown on the confirmation so the homeowner can call right away. */
  businessPhone: PropTypes.string,
  quotingMethodLabels: PropTypes.arrayOf(PropTypes.string),
  hasFreeQuotes: PropTypes.bool,
  /** Saved search request to send (existing mode). */
  wizardSearchRequestId: PropTypes.string,
  /** Unsaved service search (draft mode). Missing `timing` = ask in the sheet. */
  draftSearch: PropTypes.shape({
    serviceType: PropTypes.string.isRequired,
    subServiceType: PropTypes.string,
    timing: PropTypes.string,
    note: PropTypes.string,
  }),
  /** Up to two more top matches for the opt-in "also send" checkbox. */
  additionalRecipients: PropTypes.arrayOf(
    PropTypes.shape({
      BusinessId: PropTypes.string.isRequired,
      BusinessName: PropTypes.string,
    }),
  ),
  clientSessionId: PropTypes.string,
  user: PropTypes.object,
  /** Called with per-recipient outcomes after a successful send (sheet stays open on confirmation). */
  onSent: PropTypes.func,
  /** Existing mode: labels for the "Your request" panel. */
  serviceLabel: PropTypes.string,
  subserviceLabel: PropTypes.string,
  urgencyLabel: PropTypes.string,
  wizardState: PropTypes.object,
};

export default ContactForServiceModal;
